import React, { useEffect, useRef, useState } from 'react';

import type { BearriMotionAsset } from './motionAssets';

export type PlaybackKind = 'poster' | 'webm' | 'hevc' | 'animated-image';

export type MotionCapabilities = {
  userAgent: string;
  platform: string;
  maxTouchPoints: number;
  canPlayHevc: boolean;
  canPlayWebm: boolean;
};

export type MotionMediaProps = {
  asset: BearriMotionAsset;
  mode?: 'loop' | 'cue';
  playing?: boolean;
  className?: string;
  style?: React.CSSProperties;
  priority?: boolean;
};

let cachedCapabilities: MotionCapabilities | null = null;

function detectMotionCapabilities(): MotionCapabilities {
  if (cachedCapabilities) return cachedCapabilities;

  const probe = document.createElement('video');
  cachedCapabilities = {
    userAgent: navigator.userAgent,
    platform: navigator.platform,
    maxTouchPoints: navigator.maxTouchPoints,
    canPlayHevc: probe.canPlayType('video/mp4; codecs="hvc1"') !== '',
    canPlayWebm: probe.canPlayType('video/webm; codecs="vp9"') !== '',
  };
  return cachedCapabilities;
}

function isAppleWebKit(capabilities: MotionCapabilities): boolean {
  const ua = capabilities.userAgent;
  const isSafari = /Safari/i.test(ua)
    && !/(Chrome|Chromium|CriOS|Edg|OPR|Android)/i.test(ua);
  const isIOS = /iPad|iPhone|iPod/i.test(ua)
    || (capabilities.platform === 'MacIntel' && capabilities.maxTouchPoints > 1);
  return isSafari || isIOS;
}

export function selectPlayback(
  asset: BearriMotionAsset,
  capabilities: MotionCapabilities,
): PlaybackKind {
  // Safari says it supports VP9 but renders this codec's alpha as black. Its
  // native HEVC-with-alpha path is both smaller and fully controllable.
  if (isAppleWebKit(capabilities)) {
    if (asset.hevc && capabilities.canPlayHevc) return 'hevc';
    // WebKit's VP9 support does not imply transparency support.
    return asset.animatedFallback ? 'animated-image' : 'poster';
  }

  if (
    asset.webm
    && capabilities.canPlayWebm
  ) {
    return 'webm';
  }

  return asset.animatedFallback ? 'animated-image' : 'poster';
}

/**
 * Cross-browser transparent motion primitive.
 *
 * Chrome/Firefox receive VP9 alpha, Safari/iOS receive HEVC alpha, and an
 * animated WebP covers any engine that rejects both. Off-screen media rests on
 * the poster so it consumes neither decode time nor animation work.
 */
const MotionMedia: React.FC<MotionMediaProps> = ({
  asset,
  mode = 'loop',
  playing = true,
  className = '',
  style,
  priority = false,
}) => {
  const [kind, setKind] = useState<PlaybackKind>('poster');
  const [inView, setInView] = useState(priority);
  const hostRef = useRef<HTMLSpanElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const query = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    const update = () => setKind(query?.matches ? 'poster' : selectPlayback(asset, detectMotionCapabilities()));
    update();
    query?.addEventListener('change', update);
    return () => query?.removeEventListener('change', update);
  }, [asset]);

  useEffect(() => {
    const el = hostRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') {
      setInView(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => setInView(entry.isIntersecting),
      { rootMargin: '120px' },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const shouldPlay = inView && playing;
    if (shouldPlay) {
      if (mode === 'cue') video.currentTime = 0;
      video.play().catch(() => {
        // The poster remains visible if autoplay is interrupted by a hidden
        // tab or browser power-saving policy.
      });
    } else {
      video.pause();
      if (mode === 'cue') video.currentTime = 0;
    }
  }, [inView, kind, mode, playing]);

  const videoSource = kind === 'hevc' ? asset.hevc : asset.webm;
  const animateImage = kind === 'animated-image'
    && inView
    && playing;

  return (
    <span
      ref={hostRef}
      className="block h-full w-full"
      data-bearri-playback={kind}
      aria-hidden="true"
    >
      {kind === 'webm' || kind === 'hevc' ? (
        <video
          ref={videoRef}
          className={className}
          style={style}
          src={videoSource}
          poster={asset.poster}
          muted
          playsInline
          autoPlay={mode === 'loop' && inView && playing}
          loop={mode === 'loop'}
          preload={priority || inView ? 'auto' : 'none'}
          onError={() => setKind(asset.animatedFallback ? 'animated-image' : 'poster')}
        />
      ) : (
        <img
          key={animateImage ? asset.animatedFallback : asset.poster}
          className={className}
          style={style}
          src={animateImage ? asset.animatedFallback : asset.poster}
          alt=""
          draggable={false}
          decoding="async"
          loading={priority ? 'eager' : 'lazy'}
          onError={animateImage ? () => setKind('poster') : undefined}
        />
      )}
    </span>
  );
};

export default MotionMedia;
